import React from "react";
import { Plus, Trash2, Hotel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CuratedPhotoStrip } from "@/pages/curated/CuratedPhotoStrip";
import { uid } from "@/pages/curated/shared";
import { BOARD_TYPES, nightsBetween } from "@/pages/cotizador/shared";

export function CuratedHotelsTab({ trip, onChange }) {
  const accs = trip.accommodations || [];
  const add = () => {
    const last = accs[accs.length - 1];
    onChange({ accommodations: [...accs, { id: uid("acc"), name: "", city: "", date_from: last?.date_to || trip.start_date || null, date_to: null, board: "bb", sales_description: "", images: [] }] });
  };
  const update = (id, patch) => onChange({ accommodations: accs.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
  const remove = (id) => onChange({ accommodations: accs.filter((a) => a.id !== id) });
  return (
    <div className="space-y-4" data-testid="curated-hotels-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Los hoteles se muestran en la sección «Stay» de la landing con sus fotos y descripción.</p>
        <Button size="sm" onClick={add} data-testid="curated-add-hotel"><Plus className="mr-1.5 h-4 w-4" /> Añadir hotel</Button>
      </div>
      {accs.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="curated-hotels-empty"><Hotel className="mx-auto mb-2 h-6 w-6" /> Sin hoteles todavía.</div>
      ) : accs.map((a, i) => {
        const nights = nightsBetween(a.date_from, a.date_to);
        return (
          <Card key={a.id} data-testid={`curated-hotel-${i}`}>
            <CardContent className="space-y-3 p-4">
              <div className="grid gap-3 sm:grid-cols-[1fr_180px_150px_150px_140px_32px] sm:items-end">
                <div><Label className="text-xs">Hotel</Label><Input value={a.name || ""} onChange={(e) => update(a.id, { name: e.target.value })} placeholder="Nombre del hotel" data-testid={`curated-hotel-name-${i}`} /></div>
                <div><Label className="text-xs">Ciudad</Label><Input value={a.city || ""} onChange={(e) => update(a.id, { city: e.target.value })} /></div>
                <div><Label className="text-xs">Check-in</Label><Input type="date" value={a.date_from || ""} onChange={(e) => update(a.id, { date_from: e.target.value })} data-testid={`curated-hotel-from-${i}`} /></div>
                <div><Label className="text-xs">Check-out {nights > 0 && <span className="text-muted-foreground">· {nights} noche{nights !== 1 ? "s" : ""}</span>}</Label><Input type="date" value={a.date_to || ""} onChange={(e) => update(a.id, { date_to: e.target.value })} data-testid={`curated-hotel-to-${i}`} /></div>
                <div><Label className="text-xs">Board</Label>
                  <Select value={a.board || "bb"} onValueChange={(v) => update(a.id, { board: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{BOARD_TYPES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent></Select>
                </div>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(a.id)} aria-label="Quitar hotel" data-testid={`curated-hotel-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Textarea rows={3} value={a.sales_description || ""} onChange={(e) => update(a.id, { sales_description: e.target.value })} placeholder="Descripción del hotel para el cliente" className="text-sm" />
              <CuratedPhotoStrip images={a.images || []} onChange={(imgs) => update(a.id, { images: imgs })} query={a.name || a.city} testid={`curated-hotel-photos-${i}`} />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
